/** Typed shape, defaults and tolerant normaliser for a person's Preferences; the Swift mirror reads the same JSON. */

export type RailLabelMode = 'icons-and-text' | 'icons-only';
export type ThemeChoice = 'light' | 'dark' | 'system';
export type DensityChoice = 'clean' | 'compact';
export type EmailWhenInactive = 'never' | '15m' | '1h' | 'daily';
export type OpenConversationAt = 'oldestUnread' | 'newest';
export type DateFormatChoice = 'dmy' | 'dMonY' | 'iso';

export interface UserPreferences {
  navigation: { hidden: string[]; order: string[]; labelMode: RailLabelMode };
  appearance: { theme: ThemeChoice; density: DensityChoice; takeoffApsViewer: boolean | null };
  notifications: {
    desktop: boolean;
    mobile: boolean;
    reminderMinutes: number;
    showPreview: boolean;
    muteSounds: boolean;
    emailWhenInactive: EmailWhenInactive;
  };
  priorityContacts: { userIds: string[]; employerIds: string[] };
  availability: { autoMeeting: boolean; autoOnSite: boolean; autoAfterHours: boolean };
  messages: {
    enterSends: boolean;
    clock24h: boolean;
    linkPreviews: boolean;
    showTyping: boolean;
    openAt: OpenConversationAt;
    confirmMarkAll: boolean;
  };
  region: { timeZone: string; dateFormat: DateFormatChoice };
  accessibility: { reduceMotion: boolean; underlineLinks: boolean };
  privacy: { showInOtherWorkspaces: boolean; hiddenUserIds: string[] };
  advanced: { cmdKSearch: boolean; cmdFSearch: boolean; warnExternalShare: boolean; warnSuspiciousLinks: boolean };
}

export type PreferencesPatch = { [S in keyof UserPreferences]?: Partial<UserPreferences[S]> };

export const PREFERENCES_SCHEMA_VERSION = 1;
export const MAX_PREFERENCE_LIST_LENGTH = 200;
const MAX_ID_LENGTH = 128;
const MAX_REMINDER_MINUTES = 10080;

export const DEFAULT_PREFERENCES: UserPreferences = {
  navigation: { hidden: [], order: [], labelMode: 'icons-and-text' },
  appearance: { theme: 'system', density: 'clean', takeoffApsViewer: null },
  notifications: {
    desktop: true,
    mobile: true,
    reminderMinutes: 15,
    showPreview: true,
    muteSounds: false,
    emailWhenInactive: 'never',
  },
  priorityContacts: { userIds: [], employerIds: [] },
  // Meeting and after-hours statuses applied to everyone before this was a setting; keep that as the default.
  availability: { autoMeeting: true, autoOnSite: false, autoAfterHours: true },
  messages: {
    enterSends: true,
    clock24h: true,
    linkPreviews: true,
    showTyping: true,
    openAt: 'newest',
    confirmMarkAll: true,
  },
  region: { timeZone: 'auto', dateFormat: 'dmy' },
  accessibility: { reduceMotion: false, underlineLinks: false },
  privacy: { showInOtherWorkspaces: true, hiddenUserIds: [] },
  advanced: { cmdKSearch: true, cmdFSearch: false, warnExternalShare: true, warnSuspiciousLinks: true },
};

type Raw = Record<string, unknown>;

function asRecord(value: unknown): Raw {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Raw) : {};
}

function bool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

function idList(value: unknown, fallback: string[]): string[] {
  if (!Array.isArray(value)) return fallback;
  const seen = new Set<string>();
  for (const item of value) {
    if (typeof item === 'string' && item.length > 0 && item.length <= MAX_ID_LENGTH) seen.add(item);
    if (seen.size >= MAX_PREFERENCE_LIST_LENGTH) break;
  }
  return [...seen];
}

function reminderMinutes(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= MAX_REMINDER_MINUTES
    ? value
    : fallback;
}

function timeZone(value: unknown, fallback: string): string {
  if (typeof value !== 'string') return fallback;
  if (value === 'auto') return 'auto';
  if (value.length === 0 || value.length > 64) return fallback;
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: value });
    return value;
  } catch {
    return fallback;
  }
}

/** Read a section field-by-field so one bad value costs only that field's default. */
function normaliseSections(raw: unknown, base: UserPreferences): UserPreferences {
  const root = asRecord(raw);
  const nav = asRecord(root.navigation);
  const look = asRecord(root.appearance);
  const notif = asRecord(root.notifications);
  const prio = asRecord(root.priorityContacts);
  const avail = asRecord(root.availability);
  const msg = asRecord(root.messages);
  const region = asRecord(root.region);
  const access = asRecord(root.accessibility);
  const privacy = asRecord(root.privacy);
  const adv = asRecord(root.advanced);

  return {
    navigation: {
      hidden: idList(nav.hidden, base.navigation.hidden),
      order: idList(nav.order, base.navigation.order),
      labelMode: oneOf(nav.labelMode, ['icons-and-text', 'icons-only'], base.navigation.labelMode),
    },
    appearance: {
      theme: oneOf(look.theme, ['light', 'dark', 'system'], base.appearance.theme),
      density: oneOf(look.density, ['clean', 'compact'], base.appearance.density),
      takeoffApsViewer: look.takeoffApsViewer === null || typeof look.takeoffApsViewer === 'boolean'
        ? look.takeoffApsViewer
        : base.appearance.takeoffApsViewer,
    },
    notifications: {
      desktop: bool(notif.desktop, base.notifications.desktop),
      mobile: bool(notif.mobile, base.notifications.mobile),
      reminderMinutes: reminderMinutes(notif.reminderMinutes, base.notifications.reminderMinutes),
      showPreview: bool(notif.showPreview, base.notifications.showPreview),
      muteSounds: bool(notif.muteSounds, base.notifications.muteSounds),
      emailWhenInactive: oneOf(
        notif.emailWhenInactive,
        ['never', '15m', '1h', 'daily'],
        base.notifications.emailWhenInactive,
      ),
    },
    priorityContacts: {
      userIds: idList(prio.userIds, base.priorityContacts.userIds),
      employerIds: idList(prio.employerIds, base.priorityContacts.employerIds),
    },
    availability: {
      autoMeeting: bool(avail.autoMeeting, base.availability.autoMeeting),
      autoOnSite: bool(avail.autoOnSite, base.availability.autoOnSite),
      autoAfterHours: bool(avail.autoAfterHours, base.availability.autoAfterHours),
    },
    messages: {
      enterSends: bool(msg.enterSends, base.messages.enterSends),
      clock24h: bool(msg.clock24h, base.messages.clock24h),
      linkPreviews: bool(msg.linkPreviews, base.messages.linkPreviews),
      showTyping: bool(msg.showTyping, base.messages.showTyping),
      openAt: oneOf(msg.openAt, ['oldestUnread', 'newest'], base.messages.openAt),
      confirmMarkAll: bool(msg.confirmMarkAll, base.messages.confirmMarkAll),
    },
    region: {
      timeZone: timeZone(region.timeZone, base.region.timeZone),
      dateFormat: oneOf(region.dateFormat, ['dmy', 'dMonY', 'iso'], base.region.dateFormat),
    },
    accessibility: {
      reduceMotion: bool(access.reduceMotion, base.accessibility.reduceMotion),
      underlineLinks: bool(access.underlineLinks, base.accessibility.underlineLinks),
    },
    privacy: {
      showInOtherWorkspaces: bool(privacy.showInOtherWorkspaces, base.privacy.showInOtherWorkspaces),
      hiddenUserIds: idList(privacy.hiddenUserIds, base.privacy.hiddenUserIds),
    },
    advanced: {
      cmdKSearch: bool(adv.cmdKSearch, base.advanced.cmdKSearch),
      cmdFSearch: bool(adv.cmdFSearch, base.advanced.cmdFSearch),
      warnExternalShare: bool(adv.warnExternalShare, base.advanced.warnExternalShare),
      warnSuspiciousLinks: bool(adv.warnSuspiciousLinks, base.advanced.warnSuspiciousLinks),
    },
  };
}

/** Turn any stored value into a complete, valid UserPreferences: unknown keys dropped, bad values defaulted. */
export function normalizePreferences(raw: unknown): UserPreferences {
  return normaliseSections(raw, DEFAULT_PREFERENCES);
}

/** Lay a patch over a base, section by section, re-validating so a bad patch value keeps the base value. */
export function applyPatch(base: UserPreferences, patch: PreferencesPatch): UserPreferences {
  const merged: Raw = {};
  for (const section of Object.keys(base) as (keyof UserPreferences)[]) {
    merged[section] = { ...base[section], ...asRecord(patch[section]) };
  }
  return normaliseSections(merged, base);
}

/** Drop patch fields and sections that are not part of the schema, so the server never stores strays. */
export function sanitisePatch(patch: PreferencesPatch): PreferencesPatch {
  const probe = applyPatch(DEFAULT_PREFERENCES, patch);
  const clean: Raw = {};
  for (const section of Object.keys(patch) as (keyof UserPreferences)[]) {
    if (!(section in DEFAULT_PREFERENCES)) continue;
    const fields = asRecord(patch[section]);
    const kept: Raw = {};
    for (const field of Object.keys(fields)) {
      if (!(field in DEFAULT_PREFERENCES[section])) continue;
      const value = fields[field];
      const normalised = (probe[section] as Raw)[field];
      // Lists are cleaned (deduped, capped); any other value that did not survive validation is dropped, not defaulted.
      if (Array.isArray(value) ? Array.isArray(normalised) : value === normalised) kept[field] = normalised;
    }
    if (Object.keys(kept).length > 0) clean[section] = kept;
  }
  return clean as PreferencesPatch;
}

/** Two patches in order; later fields win, sections combine. */
export function mergePatches(first: PreferencesPatch, second: PreferencesPatch): PreferencesPatch {
  const out: Raw = { ...first };
  for (const section of Object.keys(second) as (keyof UserPreferences)[]) {
    out[section] = { ...asRecord(first[section]), ...asRecord(second[section]) };
  }
  return out as PreferencesPatch;
}
